import React, { useState, useEffect, useCallback } from 'react';
import {
  ComposedChart, Area, Line, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine, ReferenceArea
} from 'recharts';
import { Sun, Cloud, CloudRain, Wind, Thermometer, Droplets, TrendingUp, RefreshCw } from 'lucide-react';
import ApiService from '../services/apiService';

// ── Variable config ───────────────────────────────────────────────────────────
const WEATHER_VARS = [
  { key: 'temperature_c',      label: 'Temp (°C)',       unit: '°C',   color: '#f97316', icon: Thermometer, type: 'line' },
  { key: 'relative_humidity',  label: 'Humidity (%)',    unit: '%',    color: '#06b6d4', icon: Droplets,    type: 'line' },
  { key: 'precipitation_mm',   label: 'Rainfall (mm)',   unit: 'mm',   color: '#3b82f6', icon: CloudRain,   type: 'bar'  },
  { key: 'cloud_cover_pct',    label: 'Cloud Cover (%)', unit: '%',    color: '#94a3b8', icon: Cloud,       type: 'area' },
  { key: 'wind_speed_kmh',     label: 'Wind (km/h)',     unit: 'km/h', color: '#10b981', icon: Wind,        type: 'line' },
  { key: 'solar_radiation_wm2',label: 'Solar (W/m²)',    unit: 'W/m²', color: '#f59e0b', icon: Sun,         type: 'area' },
];

const INTERVALS = ['Days', 'Weeks', 'Months'];

// ── Custom Tooltip ─────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, theme, tLabel, tUnit }) => {
  if (!active || !payload || !payload.length) return null;
  const isForecast = payload[0]?.payload?.is_forecast;
  const source = payload[0]?.payload?.source;
  return (
    <div className={`rounded-xl shadow-2xl border px-4 py-3 text-xs min-w-[190px]
      ${theme === 'dark' ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-200 text-[#1B263B]'}`}>
      <p className="font-bold mb-2 flex items-center gap-1.5">
        {label}
        {isForecast && (
          <span className="text-[9px] px-1.5 py-0.5 bg-indigo-500/20 text-indigo-400 rounded-full uppercase tracking-wider font-bold">
            {tLabel ? tLabel({ key: '_forecast' }) : 'Forecast'}
          </span>
        )}
      </p>
      {payload.map(p => {
        const v = WEATHER_VARS.find(x => x.key === p.dataKey);
        if (!v || p.value == null) return null;
        return (
          <div key={p.dataKey} className="flex items-center justify-between gap-4 mb-1">
            <span style={{ color: v.color }} className="font-medium flex items-center gap-1">
              <v.icon size={10} /> {tLabel ? tLabel(v) : v.label}
            </span>
            <span className="font-bold tabular-nums">{p.value} {tUnit ? tUnit(v) : v.unit}</span>
          </div>
        );
      })}
      <p className="mt-2 text-[9px] opacity-40 uppercase tracking-wider border-t border-current/10 pt-1">
        {source === 'db' ? 'Source: DB Historical' : source === 'api' ? (isForecast ? 'Source: Open-Meteo Forecast' : 'Source: Open-Meteo Historical') : 'No data'}
      </p>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const MacroWeatherChart = ({ deviceId, theme, t }) => {
  const [interval, setInterval]     = useState('Days');
  const [chartData, setChartData]   = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [activeVars, setActiveVars] = useState(new Set(WEATHER_VARS.map(v => v.key)));
  const [forecastStart, setForecastStart] = useState(null); // label of first forecast point

  const fetchChart = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await ApiService.get(`/weather-charts/${deviceId}?interval=${interval}&user_id=1`);
      if (res.status === 'success' && Array.isArray(res.data)) {
        setChartData(res.data);
        // Find the label of the first forecast point for ReferenceArea
        const firstFc = res.data.find(d => d.is_forecast);
        setForecastStart(firstFc ? firstFc.label : null);
      } else {
        setError('No data returned from server.');
      }
    } catch (e) {
      console.error('Weather chart fetch error:', e);
      setError('Failed to fetch weather data.');
    } finally {
      setLoading(false);
    }
  }, [deviceId, interval]);

  useEffect(() => { fetchChart(); }, [fetchChart]);

  const toggleVar = (key) => {
    setActiveVars(prev => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); }
      else next.add(key);
      return next;
    });
  };

  // ── Translated label helper ──────────────────────────────────────────────────
  const VAR_LABEL_MAP = t ? {
    temperature_c:       `${t.iot.ambientTemp} (${t?.common?.units?.celsius || '\u00b0C'})`,
    relative_humidity:   `${t.dashboard.hum} (${t?.common?.units?.percent || '%'})`,
    precipitation_mm:    `${t.iot.rainfall} (${t?.common?.units?.mm || 'mm'})`,
    cloud_cover_pct:     `${t.iot.cloudCover} (${t?.common?.units?.percent || '%'})`,
    wind_speed_kmh:      `${t.iot.windSpeed} (${t?.common?.units?.km_h || 'km/h'})`,
    solar_radiation_wm2: `${t.iot.solarRad} (${t?.common?.units?.W_m2 || 'W/m\u00b2'})`,
    _forecast:           t.iot.forecast,
  } : {};
  const tLabel = (v) => VAR_LABEL_MAP[v.key] || v.label || v.key;

  const VAR_UNIT_MAP = t?.common?.units ? {
    temperature_c:       t.common.units.celsius,
    relative_humidity:   t.common.units.percent,
    precipitation_mm:    t.common.units.mm,
    cloud_cover_pct:     t.common.units.percent,
    wind_speed_kmh:      t.common.units.km_h,
    solar_radiation_wm2: t.common.units.W_m2,
  } : {};
  const tUnit = (v) => VAR_UNIT_MAP[v.key] || v.unit;

  // Interval label helper
  const intervalLabel = (iv) => {
    if (!t) return iv;
    const m = { Days: t.iot.days, Weeks: t.iot.weeks, Months: t.iot.months };
    return m[iv] || iv;
  };

  // Find the label of the last non-forecast point (the "now" divider)
  const nowLabel = (() => {
    const lastHist = [...chartData].reverse().find(d => !d.is_forecast);
    return lastHist ? lastHist.label : null;
  })();

  const hasData = chartData.some(d =>
    d.temperature_c != null || d.relative_humidity != null || d.wind_speed_kmh != null
  );

  return (
    <div className="col-span-12 mt-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
        <h3 className="text-xl font-bold text-[#1B263B] dark:text-white flex items-center gap-2">
          <Sun className="text-amber-500" size={20} />
          {t ? t.iot.macroChartTitle : 'Macro Weather Charts'}
          <span className="text-xs font-normal text-gray-400 ltr:ml-1 rtl:mr-1">{t ? t.iot.macroChartSub : 'Historical + Forecast'}</span>
        </h3>

        <div className="flex items-center gap-2">
          {/* Interval toggle */}
          <div className="inline-flex bg-white dark:bg-slate-800 rounded-xl shadow-sm p-1 border border-[#1B263B]/5 dark:border-slate-700">
            {INTERVALS.map(iv => (
              <button
                key={iv}
                onClick={() => setInterval(iv)}
                className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors
                  ${interval === iv
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-[#1B263B] dark:hover:text-white'}`}
              >
                {intervalLabel(iv)}
              </button>
            ))}
          </div>
          {/* Refresh */}
          <button
            onClick={fetchChart}
            disabled={loading}
            title="Refresh chart"
            className="p-2 rounded-xl bg-white dark:bg-slate-800 border border-[#1B263B]/5 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:text-amber-500 transition-colors disabled:opacity-40"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── Variable toggles ── */}
      <div className="flex flex-wrap gap-2 mb-4">
        {WEATHER_VARS.map(v => {
          const active = activeVars.has(v.key);
          return (
            <button
              key={v.key}
              onClick={() => toggleVar(v.key)}
              title={active ? `Hide ${tLabel(v)}` : `Show ${tLabel(v)}`}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-all
                ${active
                  ? 'text-white border-transparent shadow-sm'
                  : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-400 dark:text-gray-500'}`}
              style={active ? { backgroundColor: v.color, borderColor: v.color } : {}}
            >
              <v.icon size={11} />
              {tLabel(v)}
            </button>
          );
        })}
      </div>

      {/* ── Legend row ── */}
      <div className="flex items-center gap-5 mb-3 text-xs text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1.5">
          <span className="w-8 h-0.5 bg-gray-400 dark:bg-gray-500 inline-block rounded" />
          {t ? t.iot.historical : 'Historical'}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-8 h-0.5 bg-indigo-400/60 inline-block rounded" />
          <span className="text-indigo-400 font-semibold">{t ? t.iot.shadedForecast : 'Shaded = Forecast'}</span>
        </span>
        <TrendingUp size={12} className="text-indigo-400" />
      </div>

      {/* ── Chart card ── */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-[#1B263B]/5 dark:border-slate-700 p-5 shadow-sm">
        {!deviceId ? (
          <div className="flex justify-center items-center h-64 text-gray-400 text-sm">
            {t ? t.iot.selectDeviceChart : 'Select a device to view weather charts.'}
          </div>
        ) : loading ? (
          <div className="flex flex-col justify-center items-center h-64 gap-3">
            <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-amber-500 text-sm font-medium animate-pulse">{t ? t.iot.fetchingWeather : 'Fetching weather data\u2026'}</p>
          </div>
        ) : error ? (
          <div className="flex justify-center items-center h-64 text-red-400 text-sm">{error}</div>
        ) : !hasData ? (
          <div className="flex flex-col justify-center items-center h-64 gap-2 opacity-50">
            <Cloud size={40} className="text-gray-400" />
            <p className="text-gray-400 text-sm font-medium">
              {t ? t.iot.noWeatherYet : 'No weather data yet for this device.'}
            </p>
            <p className="text-gray-400 text-xs text-center max-w-xs">
              {t ? t.iot.noWeatherDesc : 'Data populates after the first IoT sync cycle.'}
            </p>
          </div>
        ) : (
          <div style={{ width: '100%', height: 340 }} dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  {WEATHER_VARS.filter(v => v.type === 'area').map(v => (
                    <linearGradient key={v.key} id={`grad-${v.key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={v.color} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={v.color} stopOpacity={0.02} />
                    </linearGradient>
                  ))}
                </defs>

                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={theme === 'dark' ? '#334155' : '#f1f5f9'}
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: theme === 'dark' ? '#94a3b8' : '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: theme === 'dark' ? '#94a3b8' : '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  width={36}
                />
                <Tooltip
                  content={<CustomTooltip theme={theme} tLabel={tLabel} tUnit={tUnit} />}
                  cursor={{ stroke: theme === 'dark' ? '#334155' : '#e2e8f0', strokeWidth: 1, strokeDasharray: '4 4' }}
                />

                {/* ── Forecast shading region ── */}
                {forecastStart && chartData.length > 0 && (
                  <ReferenceArea
                    x1={forecastStart}
                    x2={chartData[chartData.length - 1].label}
                    fill={theme === 'dark' ? '#6366f120' : '#6366f110'}
                    strokeOpacity={0}
                  />
                )}

                {/* ── Now divider line ── */}
                {nowLabel && (
                  <ReferenceLine
                    x={nowLabel}
                    stroke={theme === 'dark' ? '#818cf8' : '#6366f1'}
                    strokeDasharray="6 3"
                    strokeWidth={1.5}
                    label={{
                      value: t ? t.iot.nowLabel : 'Now',
                      position: 'insideTopRight',
                      fontSize: 10,
                      fill: theme === 'dark' ? '#818cf8' : '#6366f1',
                      fontWeight: 700,
                      dy: -4,
                    }}
                  />
                )}

                {/* ── Series ── */}
                {WEATHER_VARS.map(v => {
                  if (!activeVars.has(v.key)) return null;

                  if (v.type === 'bar') {
                    return (
                      <Bar
                        key={v.key}
                        dataKey={v.key}
                        name={tLabel(v)}
                        fill={v.color}
                        fillOpacity={0.75}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={22}
                      />
                    );
                  }
                  if (v.type === 'area') {
                    return (
                      <Area
                        key={v.key}
                        type="monotone"
                        dataKey={v.key}
                        name={tLabel(v)}
                        stroke={v.color}
                        strokeWidth={2}
                        fill={`url(#grad-${v.key})`}
                        connectNulls
                        dot={false}
                        activeDot={{ r: 4, fill: v.color, strokeWidth: 0 }}
                      />
                    );
                  }
                  // line
                  return (
                    <Line
                      key={v.key}
                      type="monotone"
                      dataKey={v.key}
                      name={tLabel(v)}
                      stroke={v.color}
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                      activeDot={{ r: 4, fill: v.color, strokeWidth: 0 }}
                    />
                  );
                })}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Footer note */}
        {hasData && !loading && (
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-3 text-center">
            Historical: device sensor DB · Forecast: Open-Meteo API (max ~14 days ahead) ·
            Solar radiation averaged to W/m²
          </p>
        )}
      </div>
    </div>
  );
};

export default MacroWeatherChart;
